import { useEffect, useState, useMemo } from "react";
import { X } from "lucide-react";

export default function FrostedModal({
  open,
  onClose,
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  eyebrow,                 // optional small uppercase label above the title
  enterMs = 420,
  exitMs = 260,
  easing = "cubic-bezier(0.16, 1, 0.3, 1)",
}) {
  const [visible, setVisible] = useState(open);
  // Always start hidden so the entrance transition has a from→to delta to
  // animate, even when the modal is rendered with `open=true` on mount.
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      // Double RAF guarantees the browser commits the initial `show=false`
      // styles before we flip to `show=true`, so the transition actually fires.
      requestAnimationFrame(() => requestAnimationFrame(() => setShow(true)));
    } else {
      setShow(false);
    }
  }, [open]);

  // Backdrop fades in faster than the panel; panel gets a small entrance
  // delay so the eye registers the dim before the surface slides up.
  // On exit, both go simultaneously without delay so the dismiss feels snappy.
  const backdropTiming = useMemo(
    () => ({
      transitionDuration: `${show ? 220 : exitMs}ms`,
      transitionTimingFunction: easing,
    }),
    [show, exitMs, easing]
  );
  const panelTiming = useMemo(
    () => ({
      transitionDuration: `${show ? enterMs : exitMs}ms`,
      transitionTimingFunction: easing,
      transitionDelay: show ? "60ms" : "0ms",
    }),
    [show, enterMs, exitMs, easing]
  );

  const handleTransitionEnd = (e) => {
    // Only react to the panel's own transition ending (ignore children).
    if (e.target !== e.currentTarget) return;
    if (!show) setVisible(false);
  };

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity ${
          show ? "opacity-100" : "opacity-0"
        }`}
        style={backdropTiming}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        onTransitionEnd={handleTransitionEnd}
        className={`relative z-10 w-full max-w-[640px] overflow-hidden rounded-xl border border-sky-500/20
                    bg-[#0a1224]
                    shadow-[0_30px_70px_-20px_rgba(0,0,0,0.85)]
                    ring-1 ring-white/[0.03]
                    transition-all will-change-transform will-change-opacity
                    ${
                      show
                        ? "opacity-100 translate-y-0 scale-100"
                        : "opacity-0 translate-y-6 scale-[0.97]"
                    }`}
        style={panelTiming}
      >
        {/* Top accent stripe */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/60 to-transparent" />

        <div className="relative px-7 pt-6 pb-5">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 rounded-md p-1.5 text-neutral-500 hover:text-white hover:bg-white/[0.06] transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Eyebrow row (full-width, sits above the icon+title block) */}
          <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-4">
            {eyebrow || "Whiteintel"}
          </div>

          {/* Header */}
          <div className="flex items-start gap-4 pr-8">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-sky-500/30 bg-sky-500/10">
              {icon}
            </div>
            <div className="min-w-0 pt-1">
              <h2 className="text-[17px] font-semibold text-white leading-snug tracking-tight">
                {title}
              </h2>
            </div>
          </div>

          {/* Body */}
          {description && (
            <div className="mt-5 text-sm leading-relaxed text-neutral-300/95">
              {description}
            </div>
          )}

        </div>

        {/* Footer / actions — visually separated by a hairline rule on its own
            panel so it reads like a system action bar, not free-floating buttons. */}
        {(primaryAction || secondaryAction) && (
          <div className="border-t border-white/[0.06] bg-black/20 px-7 py-4 flex items-center justify-end gap-2">
            {secondaryAction && (
              <button
                onClick={secondaryAction.onClick}
                className="rounded-md border border-sky-900/40 px-3.5 py-1.5 text-sm font-medium text-neutral-300 hover:text-white hover:bg-white/[0.04] hover:border-sky-900/60 transition-colors"
              >
                {secondaryAction.label}
              </button>
            )}
            {primaryAction && (
              <button
                onClick={primaryAction.onClick}
                className="rounded-md border border-sky-500/40 bg-sky-500/15 px-3.5 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/60 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
              >
                {primaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
